import React, { useState, useRef } from "react"
import { Editor } from "@tinymce/tinymce-react"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import "./AddPolicy.scss"
import { useNavigate } from "react-router-dom"
import { Toast } from "primereact/toast"
import PolicyApi from "Api/PolicyApi"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"

const AddPolicy = () => {
  document.title = "Add Policy"
  const [addPolicyData, setAddPolicyData] = useState({
    policyName: "",
    policyDescription: "",
    policyContent: "",
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const accessToken = localStorage.getItem("accessToken")
  const navigate = useNavigate()
  const toast = useRef(null)
  
  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Policy", link: "/Policy" },
    { label: "Add Policy" },
  ]
  
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    PolicyApi.addPolicy(addPolicyData, config)
      .then(res => {
        setAddPolicyData({
          policyName: "",
          policyDescription: "",
          policyContent: "",
        })
        setMessage("Policy added successfully!")
        setLoading(false)
        setTimeout(() => {
          navigate(-1)
        }, 1000)
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Policy added successfully!",
          life: 3000,
        })
      })
      .catch(err => {
        setMessage(err.response?.data?.error || "An error occurred!")
        setLoading(false)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: err.response?.data?.error || "An error occurred!",
          life: 3000,
        })
      })
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <div className="main-content-container">
        <Row>
          <Col xs="12">
            <div className="policy-header d-flex align-items-center justify-content-between">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Add Policy"
                handleClick={handleBreadcrumbClick}
              />
            </div>
            <Card>
              <CardBody>
                <form onSubmit={handleSubmit}>
                  <div className="policy-add-form">
                    <div className="form-group">
                      <label htmlFor="policyName" className="label">
                        Policy Name
                      </label>
                      <input
                        id="policyName"
                        type="text"
                        value={addPolicyData.policyName}
                        onChange={e =>
                          setAddPolicyData({
                            ...addPolicyData,
                            policyName: e.target.value,
                          })
                        }
                        className="form-control"
                        placeholder="Enter policy name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="policyDescription" className="label">
                        Policy Description
                      </label>
                      <textarea
                        id="policyDescription"
                        value={addPolicyData.policyDescription}
                        onChange={e =>
                          setAddPolicyData({
                            ...addPolicyData,
                            policyDescription: e.target.value,
                          })
                        }
                        className="form-control"
                        rows="4"
                        placeholder="Enter policy description..."
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="policyContent" className="label">
                        Policy Content
                      </label>
                      <Editor
                        apiKey="t7g4d6ut5ygdvwif7tp5bwyysui56413aomjuh8kusvw5qgv"
                        value={addPolicyData.policyContent}
                        onEditorChange={content =>
                          setAddPolicyData({
                            ...addPolicyData,
                            policyContent: content,
                          })
                        }
                        init={{
                          height: 500,
                          menubar: true,
                          plugins: [
                            "advlist",
                            "autolink",
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "preview",
                            "anchor",
                            "code",
                            "fullscreen",
                            "table",
                            "help",
                            "wordcount",
                          ],
                          toolbar:
                            "undo redo | formatselect | " +
                            "bold italic backcolor | alignleft aligncenter " +
                            "alignright alignjustify | bullist numlist outdent indent | " +
                            "removeformat | code | help",
                          content_css: false,
                          content_style: "",
                          valid_elements: "*[*]",
                          extended_valid_elements: "style,link[href|rel]",
                          custom_elements: "~style",
                          verify_html: false,
                          cleanup: false,
                          convert_fonts_to_spans: false,
                          remove_trailing_brs: false,
                          force_br_newlines: false,
                          force_p_newlines: false,
                          forced_root_block: false,
                          valid_children: "+body[style],+html[style]",
                          element_format: "html",
                          entities_encode: false,
                          entity_encoding: "raw",
                          preserve_cdata: true,
                          protect: [
                            /<![\s\S]*?>/g,
                            /<\?[\s\S]*?\?>/g,
                            /<style[\s\S]*?style>/g,
                          ],
                          valid_styles: {
                            "*": "*",
                          },
                          paste_as_text: false,
                          paste_enable_default_filters: false,
                          setup: function (editor) {
                            editor.on("BeforePaste", function (e) {
                              e.preventDefault()
                              const text =
                                e.clipboardData.getData("text/html") ||
                                e.clipboardData.getData("text")
                              editor.execCommand("mceInsertContent", false, text)
                            })
                          },
                        }}
                      />
                    </div>

                    <div className="form-actions">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={loading}
                        className="me-2"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          "Save Policy"
                        )}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default AddPolicy